import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import SourceCard from './SourceCard';
import '../styles/SourceReferencesDrawer.css';
import { visualizationStore } from '../stores/VisualizationStore';

const SourceReferencesDrawer = observer(({ store, containerRef }) => {
  // Safely destructure — both store guards happen AFTER hooks to keep hook count stable.
  const sources = store?.dedupedSources;
  const isDrawerOpen = store?.isDrawerOpen;
  const highlightedSourceIds = store?.highlightedSourceIds;

  const drawerRef = useRef(null);
  const [drawerStyle, setDrawerStyle] = useState({});

  // Scroll the first highlighted card into view when hover changes
  useEffect(() => {
    if (!highlightedSourceIds?.length || !drawerRef.current) return;
    const card = drawerRef.current.querySelector(`[data-source-id="${highlightedSourceIds[0]}"]`);
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightedSourceIds]);

  // Position drawer relative to modal container if provided
  useEffect(() => {
    if (!containerRef?.current || !drawerRef.current) {
      // If no container, use default fixed positioning
      setDrawerStyle({});
      return;
    }

    // Guard ResizeObserver for jsdom
    if (typeof ResizeObserver === 'undefined') return;

    const updatePosition = () => {
      const container = containerRef.current;
      if (!container) return;

      // Don't set right/position in inline styles - let CSS handle it
      setDrawerStyle({
        height: '100%',
        width: '320px'
      });
    };

    updatePosition();

    // Update on window resize or when drawer opens/closes
    window.addEventListener('resize', updatePosition);
    const resizeObserver = new ResizeObserver(updatePosition);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      resizeObserver.disconnect();
    };
  }, [containerRef, isDrawerOpen]);

  // Guards AFTER all hooks. Both legacy (no store) and new-canvas (canvas active) paths
  // return null. Hooks above run unconditionally so React's hook count stays stable.
  if (!store) return null;
  if (visualizationStore.canvasShouldShow) return null;

  const handleClose = () => store.closeDrawer();

  return (
    <div
      ref={drawerRef}
      className={`source-references-drawer ${isDrawerOpen ? 'open' : 'closed'} ${containerRef ? 'modal-drawer' : ''}`}
      style={containerRef ? drawerStyle : undefined}
    >
      <div className="drawer-header">
        <h3>🔗 Sources ({sources.length})</h3>
        <button
          className="drawer-close-button"
          onClick={handleClose}
          aria-label="Close sources drawer"
        >
          ×
        </button>
      </div>

      <div className="drawer-content">
        {sources.length === 0 ? (
          <div className="empty-state">
            <p>No sources yet.</p>
            <p>Links from the conversation will appear here.</p>
          </div>
        ) : (
          <div className="sources-list">
            {sources.map(source => (
              <SourceCard
                key={source.id}
                source={source}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default SourceReferencesDrawer;